/**
 * Table Efficiency — Operations module (Phase 1).
 *
 * Revenue-grade time intelligence for dine-in sessions. Renders the
 * CEO-view: Hero KPIs → Fix This First → Server Performance leaderboard.
 *
 * Data-honesty contract (enforced by the backend):
 *   - Scoped strictly to partner_id = inhouse_dine_in
 *   - NO slow_kitchen / long_dining language — requires KOT timestamps
 *     that are not yet persisted on the canonical layer.
 *   - When invoice_timestamp == transaction_created_at (Rista default),
 *     meta.invoice_equals_created = true → render a neutral caveat.
 *
 * Full 6-row bento (distribution, bottleneck visuals, table drill-down)
 * is deferred to a follow-up sprint per the agreed scope.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tableEfficiencyApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Clock, Users, Warning, Lightning, CurrencyInr, Info,
  ArrowUpRight, ArrowDownRight, CheckCircle, Timer, Gear,
  MagnifyingGlass, Table,
} from '@phosphor-icons/react';
import DeltaPill from '../components/DeltaPill';
import OrderTimelineDrawer from '../components/OrderTimelineDrawer';
import DateRangeFilter from '../components/DateRangeFilter';
import NcIntelligencePanel from '../components/table-efficiency/NcIntelligencePanel';
import { resolveInitialRange, isValidISO, fmtDDMMYYYY } from '../lib/dateRange';

// ─── Format helpers ────────────────────────────────────────────────────────
const fmtINR = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(2)}L`;
  if (abs >= 1_000)   return `${sign}₹${(abs / 1_000).toFixed(1)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
};
const fmtMin = (v) => (v == null ? '—' : `${Number(v).toFixed(0)} min`);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

// Category → visual weight
const CATEGORY_STYLE = {
  top_performer:      { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Top Performer' },
  on_target:          { text: 'text-cyan-400',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30',    label: 'On Target' },
  needs_improvement:  { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   label: 'Needs Improvement' },
  critical:           { text: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30',     label: 'Critical' },
  // Per-table categories (Phase 7 drill-down) — table-attributable bands.
  fast:               { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Fast' },
  slow:               { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   label: 'Slow' },
  very_slow:          { text: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30',     label: 'Very Slow' },
  unknown:            { text: 'text-zinc-500',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500/20',    label: 'Unknown' },
  insufficient_data:  { text: 'text-zinc-500',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500/20',    label: 'Insufficient Data' },
};

// Root-cause copy (Phase 1 + Phase 2 + Phase 6.1 codes)
const ROOT_CAUSE_LABEL = {
  healthy:                        { label: 'Healthy',              accent: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  settlement_delay:               { label: 'Settlement Delay',     accent: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30' },
  table_turnaround:               { label: 'Table Turnaround',     accent: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  server_closure_delay:           { label: 'Server Closure',       accent: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30' },
  // Phase 2 — KOT-trusted causes
  slow_kitchen:                   { label: 'Slow Kitchen',         accent: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30' },
  long_dining:                    { label: 'Long Dining',          accent: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  extended_ordering:              { label: 'Extended Ordering',    accent: 'text-cyan-400',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30' },
  late_ordering:                  { label: 'Late Ordering',        accent: 'text-cyan-400',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30' },
  mixed:                          { label: 'Mixed Causes',         accent: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  aggregator_handover_delay:      { label: 'Aggregator Handover',  accent: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30' },
  dine_in_load_affecting_delivery:{ label: 'Load Correlation',     accent: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  item_associated_delay:          { label: 'Item Association',     accent: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  channel_specific_delay:         { label: 'Channel Outlier',      accent: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  mixed_operational_load:         { label: 'Mixed Operational',    accent: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
};

// Channel risk pill
const RISK_STYLE = {
  healthy: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Healthy' },
  watch:   { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   label: 'Watch' },
  risk:    { text: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30',     label: 'Risk' },
  unknown: { text: 'text-zinc-500',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500/20',    label: 'Unknown' },
};

// ─── Date presets ──────────────────────────────────────────────────────────
// Preset list and date utilities now live in `lib/dateRange.js` +
// `components/DateRangeFilter.jsx` — shared with Overview, Explorer,
// Discounts, and Service Charge.

// ─── Hero KPI card ─────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, accent = '#D4FF00', icon: Icon, testId }) => (
  <div
    className="relative rounded-2xl bg-[#0F1115] border border-white/5 p-5 overflow-hidden"
    style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}
    data-testid={testId}
  >
    <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundColor: accent }} />
    <div className="flex items-start justify-between mb-3">
      <p className="text-[11px] uppercase tracking-[0.08em] text-zinc-500 font-medium">{label}</p>
      {Icon && <Icon size={14} className="text-zinc-600" weight="duotone" />}
    </div>
    <p className="text-[30px] font-semibold font-mono text-zinc-100 leading-none tabular-nums">{value}</p>
    {sub && <p className="text-[11px] text-zinc-500 mt-3 font-mono">{sub}</p>}
  </div>
);

// ─── Phase 3 — Stacked turnaround bar ────────────────────────────────────
// Visualizes one session's (or aggregate's) turnaround as
// prep + dining + settlement. Ordering span is rendered as a thin
// behavioural overlay INSIDE the dining segment (NOT additive — it's
// a sub-region of dining, not a fourth slice). See kitchen_timing.py
// for the formal model.
const STACK_COLORS = {
  prep:     { bg: 'bg-cyan-500/80',   border: 'border-cyan-400',    text: 'text-cyan-200' },
  dining:   { bg: 'bg-amber-500/70',  border: 'border-amber-400',   text: 'text-amber-100' },
  settle:   { bg: 'bg-rose-500/80',   border: 'border-rose-400',    text: 'text-rose-100' },
};

const StackedTurnaroundBar = ({
  prep,
  dining,
  settlement,
  orderingSpan,
  testId,
  compact = false,
  showLegend = false,
}) => {
  // Treat None / null / negative as 0 for the visual, but render only
  // segments that actually have value > 0.5min so the bar isn't cluttered
  // with hairline slivers.
  const p = Math.max(0, Number(prep) || 0);
  const d = Math.max(0, Number(dining) || 0);
  const s = Math.max(0, Number(settlement) || 0);
  const total = p + d + s;
  // ordering span is INSIDE dining — clamp so the overlay can never
  // exceed the dining segment width.
  const span = Math.max(0, Math.min(Number(orderingSpan) || 0, d));

  if (total <= 0) {
    return (
      <div
        className="text-[10px] text-zinc-600 italic font-mono"
        data-testid={testId}
      >
        no timing data
      </div>
    );
  }

  const segs = [
    { key: 'prep',   value: p, label: 'Prep',       color: STACK_COLORS.prep },
    { key: 'dining', value: d, label: 'Dining',     color: STACK_COLORS.dining },
    { key: 'settle', value: s, label: 'Settlement', color: STACK_COLORS.settle },
  ];
  const height = compact ? 'h-3' : 'h-6';

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className={`flex w-full ${height} rounded-md overflow-hidden bg-zinc-800/40 border border-white/5`}>
        {segs.map((seg) => {
          if (seg.value < 0.5) return null;
          const widthPct = (seg.value / total) * 100;
          // ordering span overlay sits inside dining, anchored to its
          // left edge — represents "active ordering window" within dining.
          const insideOverlay = seg.key === 'dining' && span > 0 && (
            <div
              className="absolute inset-y-0 left-0 border-r border-white/40"
              style={{ width: `${(span / d) * 100}%`, background: 'rgba(255,255,255,0.08)' }}
              title={`Ordering span — ${span.toFixed(0)} min`}
            />
          );
          return (
            <div
              key={seg.key}
              className={`relative ${seg.color.bg} flex items-center justify-center transition-all`}
              style={{ width: `${widthPct}%` }}
              title={`${seg.label} — ${seg.value.toFixed(0)} min`}
              data-testid={`${testId}-${seg.key}`}
            >
              {insideOverlay}
              {!compact && widthPct > 8 && (
                <span className={`relative z-10 text-[10px] font-mono ${seg.color.text} tabular-nums px-1 truncate`}>
                  {seg.value.toFixed(0)}m
                </span>
              )}
            </div>
          );
        })}
      </div>
      {showLegend && (
        <div className="flex items-center gap-4 text-[10px] text-zinc-400 font-mono uppercase tracking-wider">
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded ${STACK_COLORS.prep.bg}`} />Prep · first_kot − created</span>
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded ${STACK_COLORS.dining.bg}`} />Dining · bill − first_kot</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded bg-white/40" />Ordering span · last_kot − first_kot</span>
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded ${STACK_COLORS.settle.bg}`} />Settlement · closed − bill</span>
        </div>
      )}
    </div>
  );
};

// ─── Phase 2 — KOT-driven timing split ────────────────────────────────────
const TimingSplitBlock = ({ ts, meta }) => {
  if (!ts) return null;
  const coverage = ts.kot_coverage_pct ?? 0;
  const trusted = ts.trusted === true;
  const eligible = ts.eligible === true;

  // Band styling — trusted green, partial amber, absent zinc
  const band = trusted
    ? { accent: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', label: 'KOT Coverage · Trusted' }
    : eligible
      ? { accent: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/5', label: 'KOT Coverage · Below Threshold' }
      : { accent: 'text-zinc-400', border: 'border-zinc-500/20', bg: 'bg-zinc-500/5', label: 'KOT Coverage · Not Present' };

  const Cell = ({ label, value, testId, hint }) => (
    <div data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-[22px] font-mono tabular-nums ${trusted ? 'text-zinc-100' : 'text-zinc-400'}`}>
        {value == null ? '—' : `${Number(value).toFixed(0)} min`}
      </p>
      {hint && <p className="text-[10px] text-zinc-600 font-mono mt-1">{hint}</p>}
    </div>
  );

  return (
    <div
      className={`rounded-xl border ${band.border} ${band.bg} p-5`}
      data-testid="te-timing-split"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Timer size={14} className={band.accent} weight="duotone" />
          <h2 className="text-[14px] uppercase tracking-wider text-zinc-300 font-semibold">
            Timing Split · Prep vs Dining (Phase 2)
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`text-[10px] px-2 py-1 rounded border ${band.border} ${band.bg} ${band.accent} font-mono uppercase tracking-wider`}
            data-testid="te-timing-split-coverage"
          >
            {band.label} · {coverage.toFixed(0)}%
          </span>
          <span className="text-[11px] text-zinc-500 font-mono">
            {ts.sessions_with_kot}/{ts.sessions_total} sessions with KOT
          </span>
        </div>
      </div>

      {/* Phase 3 — outlet-level stacked breakdown of an average session.
          prep + dining + settlement are additive (= total turnaround);
          ordering span is shown as an overlay marker inside dining. */}
      <div className="mb-4">
        <StackedTurnaroundBar
          prep={ts.avg_prep_min}
          dining={ts.avg_dining_min}
          settlement={ts.avg_settlement_delay_min}
          orderingSpan={ts.avg_ordering_span_min}
          testId="te-outlet-stack"
          showLegend
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Cell
          label="Avg Prep"
          value={ts.avg_prep_min}
          testId="te-ts-prep"
          hint="first_kot − created"
        />
        <Cell
          label="Avg Dining"
          value={ts.avg_dining_min}
          testId="te-ts-dining"
          hint="bill − first_kot"
        />
        <Cell
          label="Ordering Span"
          value={ts.avg_ordering_span_min}
          testId="te-ts-span"
          hint="last_kot − first_kot · behavioural"
        />
        <Cell
          label="Settlement"
          value={ts.avg_settlement_delay_min}
          testId="te-ts-settlement"
          hint="closed − bill"
        />
      </div>

      {/* Late-ordering behavioural strip — shown only when trusted and the
          share is non-trivial. Behavioural, NOT a delay. */}
      {trusted && (ts.late_ordering_share_pct ?? 0) > 0 && (
        <div
          className="mt-4 pt-4 border-t border-zinc-700/40 flex items-center justify-between text-[12px]"
          data-testid="te-late-ordering-strip"
        >
          <span className="text-zinc-400">
            Late-ordering pattern · last KOT within {ts.late_ordering_proximity_min ?? 10} min of bill print
          </span>
          <span className="font-mono tabular-nums text-cyan-400">
            {Number(ts.late_ordering_share_pct ?? 0).toFixed(0)}% of sessions
          </span>
        </div>
      )}

      {!trusted && eligible && (
        <p className="text-[12px] text-amber-400/90 mt-4 pt-4 border-t border-amber-500/20">
          <Info size={12} weight="duotone" className="inline mr-1" />
          KOT coverage is {coverage.toFixed(0)}% — below the {ts.coverage_threshold_pct || 60}% trust
          threshold. Prep / dining split is shown for transparency but is NOT used
          for root-cause classification yet. Run the backfill replay to raise coverage.
        </p>
      )}
      {!eligible && (
        <p className="text-[12px] text-zinc-500 mt-4 pt-4 border-t border-zinc-500/20">
          <Info size={12} weight="duotone" className="inline mr-1" />
          No orders in this window carry per-item KOT timestamps. Prep / dining
          split becomes available once the Rista connector ingests KOT data
          (or the backfill replay is run on historical rows).
        </p>
      )}
    </div>
  );
};

// ─── Data honesty banner ──────────────────────────────────────────────────
const HonestyBanner = ({ limitations, dataConfidence }) => {
  if (!limitations || !limitations.length) return null;
  const confAccent = dataConfidence === 'low' ? 'text-amber-400' : 'text-zinc-500';
  return (
    <div
      className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex gap-3"
      data-testid="te-honesty-banner"
    >
      <Info size={18} className="text-amber-400 shrink-0 mt-0.5" weight="duotone" />
      <div className="flex-1">
        <p className="text-[12px] uppercase tracking-wider text-amber-400 mb-2 font-semibold">
          Data Limitations · Confidence:{' '}
          <span className={`font-mono ${confAccent}`}>{dataConfidence || 'unknown'}</span>
        </p>
        <ul className="space-y-1">
          {limitations.map((l, i) => (
            <li key={i} className="text-[13px] text-zinc-300 leading-relaxed">— {l}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ─── Fix This First card ──────────────────────────────────────────────────
const FixCard = ({ item, rank }) => {
  const sevStyle = {
    high:   { border: 'border-red-500/40',    glow: 'bg-red-500/10',    chip: 'text-red-400 bg-red-500/10 border-red-500/30' },
    medium: { border: 'border-amber-500/40',  glow: 'bg-amber-500/10',  chip: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
    low:    { border: 'border-emerald-500/40', glow: 'bg-emerald-500/10', chip: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  }[item.severity || 'medium'];
  return (
    <div
      className={`rounded-xl bg-[#0F1115] border ${sevStyle.border} p-5 flex flex-col gap-3`}
      data-testid={`te-fix-${rank}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`w-6 h-6 rounded-full ${sevStyle.glow} flex items-center justify-center text-[12px] font-mono font-semibold`}>
            {rank}
          </span>
          <h4 className="text-[15px] font-semibold text-zinc-100">{item.issue}</h4>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${sevStyle.chip}`}>
          {item.severity}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Est. Impact</p>
          <p className="text-[20px] font-mono text-emerald-400 tabular-nums">
            {fmtINR(item.est_revenue_impact)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Time Loss</p>
          <p className="text-[20px] font-mono text-zinc-300 tabular-nums">{fmtMin(item.impact_min)}</p>
        </div>
      </div>
      <p className="text-[12.5px] text-zinc-400 leading-relaxed border-t border-white/5 pt-3">
        {item.action}
      </p>
    </div>
  );
};

// ─── Server leaderboard row ──────────────────────────────────────────────
const ServerRow = ({ row, idx, phase2 }) => {
  const cs = CATEGORY_STYLE[row.category] || CATEGORY_STYLE.insufficient_data;
  const score = row.efficiency_score;
  // Δ pill: > 0 = slower than outlet (amber), < 0 = faster (emerald).
  // Behavioural — server-attributable via pacing / check-back cadence.
  const delta = row.dining_vs_outlet_delta_min;
  const deltaPill = delta == null
    ? null
    : delta > 5
      ? { color: 'text-amber-400 border-amber-500/30 bg-amber-500/10', sign: '+' }
      : delta < -5
        ? { color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10', sign: '' }
        : { color: 'text-zinc-500 border-zinc-500/20 bg-zinc-500/5', sign: delta > 0 ? '+' : '' };
  return (
    <tr
      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
      data-testid={`te-server-row-${idx}`}
    >
      <td className="py-3 px-4 text-zinc-500 text-[11px] font-mono">{idx + 1}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <span className="text-[14px] text-zinc-100 font-medium">{row.server}</span>
        </div>
      </td>
      <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-200 tabular-nums">{row.sessions}</td>
      <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-200 tabular-nums">
        {fmtMin(row.avg_turnaround_min)}
      </td>
      {phase2 && (
        <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-200 tabular-nums" data-testid={`te-server-dining-${idx}`}>
          <div className="flex items-center justify-end gap-2">
            <span>{fmtMin(row.avg_dining_min)}</span>
            {deltaPill && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded border ${deltaPill.color} font-mono`}
                title="Difference from outlet average dining time"
              >
                {deltaPill.sign}{Number(delta).toFixed(0)}
              </span>
            )}
          </div>
        </td>
      )}
      {phase2 && (
        <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-300 tabular-nums" data-testid={`te-server-span-${idx}`}>
          {fmtMin(row.avg_ordering_span_min)}
        </td>
      )}
      <td className="py-3 px-4 text-right font-mono text-[14px] tabular-nums">
        {score == null
          ? <span className="text-zinc-500">—</span>
          : <span className={cs.text}>{score.toFixed(0)}</span>}
      </td>
      <td className="py-3 px-4 text-right">
        <span className={`text-[10px] px-2 py-0.5 rounded border ${cs.bg} ${cs.border} ${cs.text} uppercase tracking-wider`}>
          {cs.label}
        </span>
      </td>
    </tr>
  );
};


// ─── Per-table leaderboard row (Phase 7 drill-down) ────────────────────
// Mirrors ServerRow visually so the user reads one table the same way
// they read the other. We deliberately drop the "Score" column — for
// tables, the actionable framing is "is this slot over target?", not
// a 0..150 ranking score.
const TableRow = ({ row, idx, phase2 }) => {
  const cs = CATEGORY_STYLE[row.category] || CATEGORY_STYLE.unknown;
  const delta = row.dining_vs_outlet_delta_min;
  const deltaPill = delta == null
    ? null
    : delta > 5
      ? { color: 'text-amber-400 border-amber-500/30 bg-amber-500/10', sign: '+' }
      : delta < -5
        ? { color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10', sign: '' }
        : { color: 'text-zinc-500 border-zinc-500/20 bg-zinc-500/5', sign: delta > 0 ? '+' : '' };
  const muted = row.category === 'insufficient_data';
  return (
    <tr
      className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors ${muted ? 'opacity-60' : ''}`}
      data-testid={`te-table-row-${idx}`}
    >
      <td className="py-3 px-4 text-zinc-500 text-[11px] font-mono">{idx + 1}</td>
      <td className="py-3 px-4">
        <span className="text-[14px] text-zinc-100 font-medium">{row.table}</span>
      </td>
      <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-200 tabular-nums">{row.sessions}</td>
      <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-200 tabular-nums">
        {fmtMin(row.avg_turnaround_min)}
      </td>
      {phase2 && (
        <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-200 tabular-nums" data-testid={`te-table-dining-${idx}`}>
          <div className="flex items-center justify-end gap-2">
            <span>{fmtMin(row.avg_dining_min)}</span>
            {deltaPill && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded border ${deltaPill.color} font-mono`}
                title="Difference from outlet average dining time"
              >
                {deltaPill.sign}{Number(delta).toFixed(0)}
              </span>
            )}
          </div>
        </td>
      )}
      {phase2 && (
        <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-300 tabular-nums" data-testid={`te-table-span-${idx}`}>
          {fmtMin(row.avg_ordering_span_min)}
        </td>
      )}
      <td className="py-3 px-4 text-right font-mono text-[14px] text-zinc-200 tabular-nums" data-testid={`te-table-revenue-${idx}`}>
        <div className="inline-flex items-center gap-1.5 justify-end">
          <span>{fmtINR(row.total_revenue)}</span>
          {row.revenue_change_direction && (
            <DeltaPill
              direction={row.revenue_change_direction}
              pct={row.revenue_change_pct}
              title={`prev period revenue ${row.previous_total_revenue != null ? `₹${row.previous_total_revenue.toLocaleString('en-IN')}` : '—'}`}
              size="xs"
              testId={`te-table-revenue-delta-${idx}`}
            />
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-right font-mono text-[13px] text-zinc-400 tabular-nums" data-testid={`te-table-avg-revenue-${idx}`}>
        {fmtINR(row.avg_revenue_per_session)}
      </td>
      <td className="py-3 px-4 text-right">
        <span className={`text-[10px] px-2 py-0.5 rounded border ${cs.bg} ${cs.border} ${cs.text} uppercase tracking-wider`}>
          {cs.label}
        </span>
      </td>
    </tr>
  );
};


// ─── Per-table focus card (Phase 7.6) ────────────────────────────────
// Surfaces a single table's headline metrics in a small, prominent
// block above the leaderboard when the user picks a table from the
// selector. Deliberately NOT a page-wide filter — we keep the rest of
// the surface (servers, summary, stacked bar) outlet-wide so the user
// has the comparison context inline while focusing.
const TableFocusCard = ({ row, onClear }) => {
  if (!row) return null;
  return (
    <div
      className="rounded-xl bg-[#0F1115] border border-amber-500/25 p-5 mb-4"
      data-testid="te-table-focus-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Table size={14} weight="duotone" className="text-amber-300" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
            Focused on table
          </span>
          <span className="text-[14px] font-medium text-zinc-100">{row.table}</span>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-[11px] text-zinc-500 hover:text-zinc-200 underline-offset-2 hover:underline"
          data-testid="te-table-focus-clear"
        >
          Clear focus
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Sessions</div>
          <div className="text-[28px] font-light text-zinc-100 tabular-nums" data-testid="te-focus-sessions">{row.sessions}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Total Revenue</div>
          <div className="flex items-baseline gap-2">
            <div className="text-[28px] font-light text-zinc-100 tabular-nums" data-testid="te-focus-total-revenue">{fmtINR(row.total_revenue)}</div>
            {row.revenue_change_direction && (
              <DeltaPill
                direction={row.revenue_change_direction}
                pct={row.revenue_change_pct}
                title={`prev period revenue ${row.previous_total_revenue != null ? `₹${row.previous_total_revenue.toLocaleString('en-IN')}` : '—'}`}
                size="sm"
                testId="te-focus-revenue-delta"
              />
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Avg / Session</div>
          <div className="text-[28px] font-light text-zinc-100 tabular-nums" data-testid="te-focus-avg-revenue">{fmtINR(row.avg_revenue_per_session)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Channel efficiency card (Phase 6.1) ────────────────────────────────
const ChannelCard = ({ row }) => {
  const risk = RISK_STYLE[row.risk_label] || RISK_STYLE.unknown;
  return (
    <div
      className="rounded-xl bg-[#0F1115] border border-white/5 p-4 flex flex-col gap-2"
      data-testid={`te-channel-${row.channel}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[13px] font-semibold text-zinc-100">{row.label}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
            {row.metric_label} · {row.order_count} orders
          </p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${risk.text} ${risk.bg} ${risk.border}`}>
          {risk.label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/5">
        <div>
          <p className="text-[10px] text-zinc-500">Avg</p>
          <p className="text-[15px] font-mono text-zinc-200 tabular-nums">{fmtMin(row.avg_open_to_close_min)}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500">p50</p>
          <p className="text-[15px] font-mono text-zinc-200 tabular-nums">{fmtMin(row.p50_open_to_close_min)}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500">p90</p>
          <p className="text-[15px] font-mono text-zinc-300 tabular-nums">{fmtMin(row.p90_open_to_close_min)}</p>
        </div>
      </div>
    </div>
  );
};

// ─── Aggregator handover card (Phase 6.1) ──────────────────────────────
const HandoverCard = ({ ch, threshold }) => {
  const isHigh = ch.avg_handover_min > threshold;
  return (
    <div
      className={`rounded-xl bg-[#0F1115] border p-4 ${isHigh ? 'border-red-500/30' : 'border-white/5'}`}
      data-testid={`te-handover-${ch.channel}`}
    >
      <p className="text-[13px] font-semibold text-zinc-100 mb-3">{ch.label}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Avg Handover</p>
          <p className={`text-[22px] font-mono tabular-nums ${isHigh ? 'text-red-400' : 'text-zinc-200'}`}>
            {fmtMin(ch.avg_handover_min)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">p90</p>
          <p className="text-[22px] font-mono text-zinc-300 tabular-nums">{fmtMin(ch.p90_handover_min)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Delayed</p>
          <p className="text-[15px] font-mono text-amber-400 tabular-nums">
            {ch.delayed_orders_count} ({fmtPct(ch.delayed_orders_pct)})
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Worst Hour</p>
          <p className="text-[14px] font-mono text-zinc-300 tabular-nums">
            {ch.worst_hour_bucket?.label || '—'}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Load correlation row (Phase 6.1) ───────────────────────────────────
const LoadRow = ({ bucket }) => (
  <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`te-load-${bucket.bucket}`}>
    <td className="py-3 px-4 text-[13px] font-mono text-zinc-200">{bucket.bucket} tables</td>
    <td className="py-3 px-4 text-right font-mono text-[13px] text-zinc-300 tabular-nums">{bucket.order_count}</td>
    <td className="py-3 px-4 text-right font-mono text-[13px] text-zinc-200 tabular-nums">{fmtMin(bucket.avg_handover_min)}</td>
    <td className="py-3 px-4 text-right font-mono text-[13px] text-zinc-300 tabular-nums">{fmtMin(bucket.p90_handover_min)}</td>
    <td className="py-3 px-4 text-right font-mono text-[13px] tabular-nums">
      {bucket.delayed_pct == null
        ? <span className="text-zinc-600">—</span>
        : <span className={bucket.delayed_pct > 20 ? 'text-red-400' : 'text-zinc-300'}>{fmtPct(bucket.delayed_pct)}</span>}
    </td>
  </tr>
);

// ─── Item association row (Phase 6.1) ──────────────────────────────────
const ItemAssocRow = ({ item, idx }) => (
  <tr className="border-b border-white/5" data-testid={`te-item-assoc-${idx}`}>
    <td className="py-3 px-4 text-[13px] text-zinc-100">{item.item_name}</td>
    <td className="py-3 px-4 text-right font-mono text-[13px] text-zinc-300 tabular-nums">{item.appearance_count}</td>
    <td className="py-3 px-4 text-right font-mono text-[13px] text-amber-400 tabular-nums">{item.delayed_order_count}</td>
    <td className="py-3 px-4 text-right font-mono text-[13px] text-red-400 tabular-nums">+{fmtMin(item.delay_lift_min)}</td>
  </tr>
);

// ─── Operational diagnosis strip (Phase 6.1) ───────────────────────────
const OperationalDiagnosis = ({ diag }) => {
  if (!diag) return null;
  const meta = ROOT_CAUSE_LABEL[diag.primary_issue] || ROOT_CAUSE_LABEL.mixed;
  const impactChip = {
    high:   'bg-red-500/10 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    low:    'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  }[diag.impact] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30';
  return (
    <div className={`rounded-xl border ${meta.border} ${meta.bg} p-4`} data-testid="te-operational-diagnosis">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Info size={14} className={meta.accent} weight="duotone" />
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">What appears to slow operations?</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${impactChip}`}>
          {diag.impact || '—'}
        </span>
      </div>
      <p className={`text-[16px] font-semibold ${meta.accent}`}>{meta.label}</p>
      <p className="text-[12px] text-zinc-400 mt-2 leading-relaxed">{diag.explanation}</p>
    </div>
  );
};

// ─── Root-cause pill (existing Phase 1) ─────────────────────────────────
const RootCausePill = ({ rootCause }) => {
  const meta = ROOT_CAUSE_LABEL[rootCause?.primary_issue] || ROOT_CAUSE_LABEL.mixed;
  const impactChip = {
    high:   'bg-red-500/10 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    low:    'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  }[rootCause?.impact] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30';
  return (
    <div className={`rounded-xl border ${meta.border} ${meta.bg} p-4`} data-testid="te-root-cause">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Warning size={14} className={meta.accent} weight="duotone" />
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">Primary Issue</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${impactChip}`}>
          {rootCause?.impact || '—'}
        </span>
      </div>
      <p className={`text-[16px] font-semibold ${meta.accent}`}>{meta.label}</p>
      <p className="text-[12px] text-zinc-400 mt-2 leading-relaxed">
        {rootCause?.explanation || '—'}
      </p>
    </div>
  );
};

// ─── Date range controls ────────────────────────────────────────────────
// The page-level range filter now uses the shared DateRangeFilter (see
// import above). The old `useRangePreset` hook ranged over 7/14/30 days
// only and defaulted to 30d. It has been removed so this page joins the
// canonical contract: This Month by default, full preset list including
// Last 90d / This Month / Last Month / Custom, URL-driven.

// ═══════════════════════════════════════════════════════════════════════
// Page
// ═══════════════════════════════════════════════════════════════════════

export default function TableEfficiencyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = resolveInitialRange(searchParams);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo,   setDateTo]   = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Order drill-down drawer (Phase 6 — /sale/{order_id}/timeline).
  const [drillOrderId, setDrillOrderId] = useState(null);
  const [orderLookup, setOrderLookup] = useState('');
  // Per-table focus mode (Phase 7.6) — URL-synced via `?table=...`.
  const [selectedTable, setSelectedTable] = useState(searchParams.get('table') || null);

  // Sync URL when the applied range or focused table changes.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set('date_from', dateFrom);
    sp.set('date_to', dateTo);
    if (selectedTable) {
      sp.set('table', selectedTable);
    } else {
      sp.delete('table');
    }
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, selectedTable]);

  const handleRangeChange = (from, to) => {
    if (!isValidISO(from) || !isValidISO(to)) {
      toast.error('Invalid dates'); return;
    }
    setDateFrom(from); setDateTo(to);
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    tableEfficiencyApi
      .get({ date_from: dateFrom, date_to: dateTo, comparison_mode: 'auto' })
      .then((res) => { if (alive) setData(res.data); })
      .catch((err) => {
        if (!alive) return;
        const msg = err.response?.data?.detail || err.message || 'Failed to load';
        setError(String(msg));
        toast.error('Failed to load Table Efficiency');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070B10] text-white p-6" data-testid="te-loading">
        <div className="animate-pulse space-y-4 max-w-[1400px] mx-auto">
          <div className="h-10 w-72 bg-white/5 rounded" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-white/5 rounded-2xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#070B10] text-white p-6" data-testid="te-error">
        <div className="max-w-xl mx-auto mt-16 rounded-xl border border-red-500/30 bg-red-500/10 p-5">
          <p className="text-red-400 font-semibold mb-2">Could not load Table Efficiency</p>
          <p className="text-zinc-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const rev = data?.revenue_loss || {};
  const summary = data?.summary || {};
  const meta = data?.meta || {};
  const fixes = data?.fix_this_first || [];
  const servers = data?.servers || [];
  const tables = data?.tables || [];

  return (
    <div className="min-h-screen bg-[#070B10] text-white" data-testid="table-efficiency-page">
      <div className="px-4 lg:px-6 py-5 space-y-5 max-w-[1700px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Timer size={22} className="text-[#D4FF00]" weight="duotone" />
              <h1 className="text-[24px] font-semibold tracking-tight text-zinc-100">
                Table Efficiency
              </h1>
            </div>
            <p className="text-[13px] text-zinc-500 mt-1" data-testid="te-subtitle">
              Revenue intelligence for dine-in turnaround · {fmtDDMMYYYY(dateFrom)} → {fmtDDMMYYYY(dateTo)}
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="te-range-picker">
            {/* Order lookup — drill into any order_id */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = orderLookup.trim();
                if (v) setDrillOrderId(v);
              }}
              className="flex items-center gap-1"
              data-testid="te-order-lookup-form"
            >
              <div className="flex items-center gap-1 bg-[#0F1115] border border-white/10 rounded-lg px-2 py-1 focus-within:border-[#D4FF00]/40">
                <MagnifyingGlass size={12} className="text-zinc-500" />
                <input
                  value={orderLookup}
                  onChange={(e) => setOrderLookup(e.target.value)}
                  placeholder="Order ID"
                  className="bg-transparent text-[12px] text-zinc-200 w-24 focus:outline-none placeholder:text-zinc-600"
                  data-testid="te-order-lookup-input"
                />
              </div>
            </form>
            <DateRangeFilter
              value={{ from: dateFrom, to: dateTo }}
              onChange={handleRangeChange}
              testIdPrefix="te-range"
            />
          </div>
        </div>

        {/* Honesty banner (data limitations) */}
        <HonestyBanner limitations={meta.limitations} dataConfidence={meta.data_confidence} />

        {/* Row 1 — HERO */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="te-hero">
          <KpiCard
            label="Avg Turnaround"
            value={fmtMin(summary.avg_turnaround_min)}
            sub={`Target ${summary.target_turnaround_min} min · median ${fmtMin(summary.median_turnaround_min)}`}
            accent="#D4FF00"
            icon={Clock}
            testId="te-kpi-turnaround"
          />
          <KpiCard
            label="Lost Revenue"
            value={fmtINR(rev.lost_revenue)}
            sub={
              rev.eligible
                ? `${rev.total_lost_turns} lost turns · AOV ${fmtINR(rev.avg_order_value)}`
                : (rev.reason === 'at_or_above_target'
                    ? 'Turnaround at or above target'
                    : 'Insufficient sessions')
            }
            accent="#EF4444"
            icon={CurrencyInr}
            testId="te-kpi-lost-revenue"
          />
          <KpiCard
            label="Tables > 120 min"
            value={fmtPct(summary.tables_over_target_pct)}
            sub={`${summary.tables_over_target_count || 0} of ${summary.sessions_analyzed || 0} sessions`}
            accent="#F59E0B"
            icon={Warning}
            testId="te-kpi-over-target"
          />
          <KpiCard
            label="Sessions Analyzed"
            value={summary.sessions_analyzed ?? '—'}
            sub={`${summary.sessions_skipped || 0} skipped · ${rev.active_tables || 0} tables · ${rev.business_days || 0} days`}
            accent="#06B6D4"
            icon={Users}
            testId="te-kpi-sessions"
          />
        </div>

        {/* Root cause pill (Phase 1 dine-in) */}
        <RootCausePill rootCause={data?.root_cause} />

        {/* Phase 2 — KOT-driven timing split */}
        <TimingSplitBlock ts={data?.timing_split} meta={meta} />

        {/* ══ Phase 6.1 — Channel & Load Efficiency ══════════════════ */}

        {/* Row A — Channel Efficiency */}
        <div data-testid="te-channel-efficiency">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[14px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Gear size={14} weight="duotone" />
              Channel Efficiency
            </h2>
            <p className="text-[11px] text-zinc-500">
              Dine-in shows turnaround · Delivery shows handover time
            </p>
          </div>
          {(data?.channel_efficiency || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {data.channel_efficiency.map((c) => <ChannelCard key={c.channel} row={c} />)}
            </div>
          ) : (
            <div className="rounded-xl bg-[#0F1115] border border-white/5 p-5 text-center text-[13px] text-zinc-500">
              No channel data in this window.
            </div>
          )}

          {/* Unclassified disclosure — small, muted, warning-not-danger.
              Appears only when there ARE unclassified orders; never styled
              like an actionable category. If threshold breached (> 10%),
              the same data-quality item also surfaces inside Fix This
              First as a ranked action. */}
          {data?.unclassified?.order_count > 0 && (
            <div
              className="mt-3 flex items-start gap-2 text-[12px] text-zinc-500"
              data-testid="te-unclassified-disclosure"
            >
              <Info size={14} className="text-amber-500/70 shrink-0 mt-0.5" weight="duotone" />
              <div>
                <p>
                  {data.unclassified.order_count} orders
                  ({fmtPct(data.unclassified.order_pct)}) could not be classified into a channel
                  {data.unclassified.avg_closure_time_min != null && (
                    <>
                      {'. Their average closure time is '}
                      <span className="font-mono text-zinc-400">
                        {fmtMin(data.unclassified.avg_closure_time_min)}
                      </span>
                      {data.unclassified.vs_overall_delta_min != null && (
                        <> (vs {fmtMin(data.unclassified.avg_closure_time_min - data.unclassified.vs_overall_delta_min)} overall)</>
                      )}
                    </>
                  )}
                  .
                </p>
                {data.unclassified.threshold_breached && (
                  <p className="mt-1 text-amber-400/80" data-testid="te-unclassified-threshold">
                    Unclassified share is above 10% — review POS tagging or channel mapping.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Row B — Delivery Handover Pressure */}
        {(data?.aggregator_handover?.channels?.length || 0) > 0 && (
          <div data-testid="te-handover-pressure">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[14px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Warning size={14} weight="duotone" />
                Delivery Handover Pressure
              </h2>
              <p className="text-[11px] text-zinc-500">
                Threshold: &gt;{data.aggregator_handover.threshold_min} min
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.aggregator_handover.channels.map((ch) => (
                <HandoverCard key={ch.channel} ch={ch} threshold={data.aggregator_handover.threshold_min} />
              ))}
            </div>
          </div>
        )}

        {/* Row C — Load Correlation + Item Associations */}
        {data?.load_correlation?.eligible && (
          <div data-testid="te-load-correlation">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[14px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Users size={14} weight="duotone" />
                Load Correlation
              </h2>
              <p className="text-[11px] text-zinc-500">
                Delivery handover vs. live dine-in tables at order open
              </p>
            </div>
            {data.load_correlation.insight && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-3 flex gap-3">
                <Info size={16} className="text-amber-400 shrink-0 mt-0.5" weight="duotone" />
                <div>
                  <p className="text-[13px] text-amber-300">{data.load_correlation.insight.message}</p>
                  <p className="text-[11px] text-zinc-500 mt-1 italic">
                    {data.load_correlation.insight.caveat}
                  </p>
                </div>
              </div>
            )}
            <div className="rounded-xl bg-[#0F1115] border border-white/5 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <th className="py-2 px-4 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Live Tables</th>
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Orders</th>
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Avg Handover</th>
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">p90</th>
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Delayed %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.load_correlation.buckets.map((b) => <LoadRow key={b.bucket} bucket={b} />)}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Item associations — surfaces only when gates are met */}
        {(data?.item_delay_associations?.length || 0) > 0 && (
          <div data-testid="te-item-associations">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[14px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Warning size={14} weight="duotone" />
                Items Associated with Higher Handover
              </h2>
              <p className="text-[11px] text-zinc-500 italic">Correlation — not proof of cause.</p>
            </div>
            <div className="rounded-xl bg-[#0F1115] border border-white/5 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <th className="py-2 px-4 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Item</th>
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Orders</th>
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Delayed</th>
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Lift</th>
                  </tr>
                </thead>
                <tbody>
                  {data.item_delay_associations.map((it, idx) => (
                    <ItemAssocRow key={it.item_name + idx} item={it} idx={idx} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Operational diagnosis — always shown, just below Phase 6.1 rows */}
        <OperationalDiagnosis diag={data?.operational_diagnosis} />

        {/* Row 2 — FIX THIS FIRST (combined Phase 1 + Phase 6.1) */}
        <div data-testid="te-fix-this-first">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[14px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Lightning size={14} weight="duotone" />
              Fix This First
            </h2>
            {!fixes.length && (
              <span className="text-[11px] text-zinc-500">
                {(data?.period?.fairness?.is_fair === false)
                  ? 'Unfair comparison — actions hidden until a fair window is selected'
                  : 'No structural delays detected'}
              </span>
            )}
          </div>
          {fixes.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fixes.map((f, i) => (
                <FixCard key={f.code + i} item={f} rank={i + 1} />
              ))}
            </div>
          )}
        </div>

        {/* Row 3 — SERVER PERFORMANCE */}
        <div data-testid="te-server-performance">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[14px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Users size={14} weight="duotone" />
              Server Performance
            </h2>
            <p className="text-[11px] text-zinc-500">
              Benchmark {summary.target_turnaround_min} min · min {5} sessions for score
              {data?.meta?.phase2_enabled && (
                <span className="ml-2 text-cyan-400">· KOT-trusted: dining Δ vs outlet avg shown</span>
              )}
            </p>
          </div>
          <div className="rounded-xl bg-[#0F1115] border border-white/5 overflow-hidden">
            <table className="w-full" data-testid="te-server-table">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="py-2 px-4 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-medium w-12">#</th>
                  <th className="py-2 px-4 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Server</th>
                  <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Sessions</th>
                  <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Avg Turnaround</th>
                  {data?.meta?.phase2_enabled && (
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">
                      Avg Dining <span className="text-zinc-600 normal-case">(Δ vs outlet)</span>
                    </th>
                  )}
                  {data?.meta?.phase2_enabled && (
                    <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Ordering Span</th>
                  )}
                  <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Score</th>
                  <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Category</th>
                </tr>
              </thead>
              <tbody>
                {servers.length === 0 ? (
                  <tr>
                    <td colSpan={data?.meta?.phase2_enabled ? 9 : 7} className="py-10 text-center text-zinc-500 text-[13px]">
                      No qualifying server data in this window.
                    </td>
                  </tr>
                ) : (
                  servers.map((row, idx) => (
                    <ServerRow key={row.server + idx} row={row} idx={idx} phase2={!!data?.meta?.phase2_enabled} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Phase 3 — per-server time breakdown (stacked bars). Visual
              companion to the leaderboard table; same data, different
              encoding. Shows where each server's time goes — kitchen
              (shared), dining (server-controllable via pacing), or
              settlement (POS / approvals). Easier to spot the dining
              outlier here than scanning a column of numbers. */}
          {data?.meta?.phase2_enabled && servers.length > 0 && (
            <div className="rounded-xl bg-[#0F1115] border border-white/5 p-5 mt-4" data-testid="te-server-stacks">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[12px] uppercase tracking-wider text-zinc-400 font-semibold">
                  Time Breakdown by Server
                </h3>
                <p className="text-[11px] text-zinc-500">
                  Bar width ∝ avg turnaround · same scale across rows
                </p>
              </div>
              {/* Compute the max turnaround so all bars share scale; rows
                  with shorter sessions render proportionally narrower —
                  this lets a fast server's bar literally LOOK shorter. */}
              {(() => {
                const maxTurn = Math.max(...servers.map(s => s.avg_turnaround_min || 0), 1);
                return (
                  <div className="space-y-2.5">
                    {servers.map((row, idx) => {
                      const widthPct = ((row.avg_turnaround_min || 0) / maxTurn) * 100;
                      return (
                        <div key={`stack-${row.server}-${idx}`} className="grid grid-cols-[160px_1fr_60px] gap-3 items-center" data-testid={`te-server-stack-${idx}`}>
                          <div className="text-[12px] text-zinc-200 truncate">
                            <span className="text-zinc-500 font-mono mr-2">{idx + 1}.</span>
                            {row.server}
                          </div>
                          <div style={{ width: `${widthPct}%` }} className="min-w-[80px]">
                            <StackedTurnaroundBar
                              prep={row.avg_prep_min}
                              dining={row.avg_dining_min}
                              settlement={row.avg_settlement_delay_min}
                              orderingSpan={row.avg_ordering_span_min}
                              testId={`te-server-stack-bar-${idx}`}
                              compact
                            />
                          </div>
                          <div className="text-right font-mono text-[12px] text-zinc-300 tabular-nums">
                            {row.avg_turnaround_min != null ? `${row.avg_turnaround_min.toFixed(0)}m` : '—'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Row 4 — PER-TABLE DRILL-DOWN (Phase 7 — 2026-06-01)
            Mirrors the server leaderboard structure 1:1 so the
            mental model is one click: "I see which servers are slow.
            Now I see which TABLES are slow." Same StackedTurnaroundBar
            visual + same metric set. Tables with <3 usable sessions
            are pushed below a divider with an `Insufficient data`
            tag so the lead row is always evidence-backed. */}
        <div data-testid="te-table-performance" className="mt-8">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex flex-col">
              <h2 className="text-[14px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Table size={14} weight="duotone" />
                Table Performance
              </h2>
              {data?.revenue_comparison && (
                <p className="text-[10.5px] text-zinc-500 mt-1 font-mono" data-testid="te-table-revenue-comparison-caption">
                  Revenue Δ <span className="text-zinc-400">{data.revenue_comparison.current_from} → {data.revenue_comparison.current_to}</span> <span className="text-zinc-700">vs</span> <span className="text-zinc-400">{data.revenue_comparison.previous_from} → {data.revenue_comparison.previous_to}</span> <span className="text-zinc-700">· {data.revenue_comparison.label}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 ml-auto">
              {/* Table selector — Phase 7.6 focus mode. URL-synced via
                  `?table=...`. Only scopes the focus card; the rest of
                  the page stays outlet-wide so the user has comparison
                  context inline. */}
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                Focus
              </label>
              <select
                value={selectedTable || ''}
                onChange={(e) => setSelectedTable(e.target.value || null)}
                data-testid="te-table-selector"
                className="text-[12px] font-mono bg-black/40 border border-white/5 text-zinc-200 rounded px-2 py-1 focus:outline-none focus:border-amber-500/40"
              >
                <option value="">All tables</option>
                {tables.map((t) => (
                  <option key={t.table} value={t.table}>{t.table}</option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500">
                Benchmark {summary.target_turnaround_min} min · min {3} sessions for category
                {data?.meta?.phase2_enabled && (
                  <span className="ml-2 text-cyan-400">· KOT-trusted</span>
                )}
              </p>
            </div>
          </div>

          {/* Focus card — visible only when a table is selected */}
          <TableFocusCard
            row={tables.find((t) => t.table === selectedTable)}
            onClear={() => setSelectedTable(null)}
          />

          {tables.length === 0 ? (
            <div className="rounded-xl bg-[#0F1115] border border-white/5 p-8 text-center text-[13px] text-zinc-500" data-testid="te-table-empty">
              No qualifying table data in this window.
            </div>
          ) : (
            <>
              <div className="rounded-xl bg-[#0F1115] border border-white/5 overflow-hidden">
                <table className="w-full" data-testid="te-table-leaderboard">
                  <thead>
                    <tr className="border-b border-white/5 bg-white/[0.02]">
                      <th className="py-2 px-4 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-medium w-12">#</th>
                      <th className="py-2 px-4 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Table</th>
                      <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Sessions</th>
                      <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Avg Turnaround</th>
                      {data?.meta?.phase2_enabled && (
                        <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">
                          Avg Dining <span className="text-zinc-600 normal-case">(Δ vs outlet)</span>
                        </th>
                      )}
                      {data?.meta?.phase2_enabled && (
                        <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Ordering Span</th>
                      )}
                      <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Total Revenue</th>
                      <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Avg / Session</th>
                      <th className="py-2 px-4 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tables.map((row, idx) => (
                      <TableRow key={row.table + idx} row={row} idx={idx} phase2={!!data?.meta?.phase2_enabled} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Per-table stacked turnaround visual — only when KOT trust gate passes */}
              {data?.meta?.phase2_enabled && (() => {
                const visualTables = tables.filter((t) => t.category !== 'insufficient_data');
                if (visualTables.length === 0) return null;
                const maxTurn = Math.max(...visualTables.map((t) => t.avg_turnaround_min || 0), 1);
                return (
                  <div className="rounded-xl bg-[#0F1115] border border-white/5 p-5 mt-4" data-testid="te-table-stacks">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-[12px] uppercase tracking-wider text-zinc-400 font-semibold">
                        Time Breakdown by Table
                      </h3>
                      <p className="text-[11px] text-zinc-500">
                        Bar width ∝ avg turnaround · same scale across rows
                      </p>
                    </div>
                    <div className="space-y-2.5">
                      {visualTables.map((row, idx) => {
                        const widthPct = ((row.avg_turnaround_min || 0) / maxTurn) * 100;
                        return (
                          <div key={`tstack-${row.table}-${idx}`} className="grid grid-cols-[160px_1fr_60px] gap-3 items-center" data-testid={`te-table-stack-${idx}`}>
                            <div className="text-[12px] text-zinc-200 truncate">
                              <span className="text-zinc-500 font-mono mr-2">{idx + 1}.</span>
                              {row.table}
                            </div>
                            <div style={{ width: `${widthPct}%` }} className="min-w-[80px]">
                              <StackedTurnaroundBar
                                prep={row.avg_prep_min}
                                dining={row.avg_dining_min}
                                settlement={row.avg_settlement_delay_min}
                                orderingSpan={row.avg_ordering_span_min}
                                testId={`te-table-stack-bar-${idx}`}
                                compact
                              />
                            </div>
                            <div className="text-right font-mono text-[12px] text-zinc-300 tabular-nums">
                              {row.avg_turnaround_min != null ? `${row.avg_turnaround_min.toFixed(0)}m` : '—'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>

      </div>

      {/* NC Intelligence — extension bento */}
      <div className="px-4 lg:px-6 pb-6 max-w-[1700px] mx-auto">
        <NcIntelligencePanel
          outletId={undefined}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onOrderClick={(oid) => setDrillOrderId(oid)}
        />
      </div>

      {/* Order drill-down drawer — Phase 6 */}
      <OrderTimelineDrawer
        orderId={drillOrderId}
        open={!!drillOrderId}
        onClose={() => setDrillOrderId(null)}
      />
    </div>
  );
}
